/**
 * COMMS's dice roller: the die button left of SEND and the compact composer it opens above the input
 * (`dice-rework` 1.1). Bonus, accuracy and difficulty only, through the same `useCheckComposer` DICE.app
 * uses, and ROLL sends exactly the shown notation via `session.postChat`, landing as the same `/roll`
 * card. Every role gets it; for the GM it is also where a sheet's roll button lands (`preselect`, from
 * `shell/railDice.ts`), since the GM has no DICE page.
 *
 * After a successful roll the counters reset and the popover closes: the card in the feed is the
 * record, and a stale accuracy die carried into the next check is the worse failure. A refused post
 * (link down) keeps both, with the not-sent note.
 */
import { faDiceD20 } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useId, useRef, useState } from "react";
import { Icon } from "../../lib/Icon";
import { useGameSessionContext } from "../../lib/game/GameSessionProvider";
import { NotSentNote, useNotSentNote } from "../../lib/linkHealth";
import { useCheckComposer, type PreselectBonus } from "./checkComposer";
import { DiceStepper } from "./DiceStepper";
import "./comms-dice.css";

export function CommsDice({ preselect }: { preselect?: PreselectBonus | null }) {
  const session = useGameSessionContext();
  const { notSent, report } = useNotSentNote();
  const composer = useCheckComposer(preselect);
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  // A new preselect after mount opens the popover; the one present at mount was already handled
  // (it seeded the counters) and must not pop the composer open on a scene change's remount.
  const mountedPreselect = useRef(preselect);
  useEffect(() => {
    if (preselect != null && preselect !== mountedPreselect.current) setOpen(true);
  }, [preselect]);

  const close = (refocus: boolean) => {
    setOpen(false);
    if (refocus) buttonRef.current?.focus();
  };

  // Esc and an outside press close it. Esc hands focus back to the die button; an outside press
  // leaves focus wherever that press put it.
  useEffect(() => {
    if (!open) return;
    popoverRef.current?.querySelector<HTMLButtonElement>("button:not(:disabled)")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close(true);
      }
    };
    const onPointer = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!popoverRef.current?.contains(t) && !buttonRef.current?.contains(t)) close(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  const roll = () => {
    if (!report(session.postChat(composer.notation))) return;
    composer.reset();
    close(true);
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className={`comms-dice-toggle${open ? " open" : ""}`}
        aria-label="Roll dice"
        aria-expanded={open}
        aria-controls={popoverId}
        disabled={session.actorId === null}
        onClick={() => (open ? close(true) : setOpen(true))}
      >
        <Icon icon={faDiceD20} />
      </button>
      {open && (
        <div ref={popoverRef} id={popoverId} className="comms-dice-pop" role="dialog" aria-label="Dice roll">
          <div className="comms-dice-head">CHECK · 1D20 + BONUS ± ACC/DIFF</div>
          <DiceStepper
            look="comms"
            label={composer.source ? `Bonus · ${composer.source}` : "Bonus"}
            value={composer.bonus}
            onChange={composer.setBonus}
          />
          <DiceStepper look="comms" label="Accuracy" value={composer.accuracy} onChange={composer.setAccuracy} min={0} tone="acc" />
          <DiceStepper look="comms" label="Difficulty" value={composer.difficulty} onChange={composer.setDifficulty} min={0} tone="diff" />
          <div className="comms-dice-foot">
            <span className="comms-dice-preview" aria-live="polite">
              {composer.notation}
            </span>
            <button type="button" className="comms-dice-roll" onClick={roll}>
              ROLL
            </button>
          </div>
          <NotSentNote show={notSent} />
        </div>
      )}
    </>
  );
}
